import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, X, Trash2, ImagePlus, MapPin, Calendar, Feather, Loader2, Heart, MessageCircle, Send, Link2, ExternalLink, LayoutGrid, Globe2, Hourglass } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Spinner, SectionLabel, Button, Pill } from "../components/Primitives";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export const REGIONS = [
  "West Africa", "East Africa", "Central Africa", "Southern Africa", "North Africa",
  "Horn of Africa", "Caribbean", "North America", "South America", "Europe", "Arabian Peninsula",
];

export function photoSrc(url) {
  if (!url) return "";
  return url.startsWith("http") ? url : `${BACKEND}${url}`;
}

export default function Stories() {
  const { user, login } = useAuth();
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [active, setActive] = useState(null);

  const load = async () => {
    const r = await api.get("/stories");
    setStories(r.data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const removeStory = async (id) => {
    await api.delete(`/stories/${id}`);
    toast.success("Story removed");
    setActive(null);
    load();
  };

  const updateCounts = (id, counts) => {
    setStories((prev) => prev.map((s) => (s.id === id ? { ...s, ...counts } : s)));
  };

  const [groupBy, setGroupBy] = useState(() => {
    const g = new URLSearchParams(window.location.search).get("group");
    return ["region", "era"].includes(g) ? g : "none";
  });
  const setGroup = (val) => {
    setGroupBy(val);
    const url = val === "none" ? window.location.pathname : `${window.location.pathname}?group=${val}`;
    window.history.replaceState({}, "", url);
  };

  useEffect(() => {
    if (loading) return;
    const focus = new URLSearchParams(window.location.search).get("focus");
    if (!focus || groupBy === "none") return;
    const t = setTimeout(() => {
      const el = document.getElementById(`group-${focus}`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 300);
    return () => clearTimeout(t);
  }, [loading, groupBy]);

  const copyGroupLink = async (key) => {
    const url = `${window.location.origin}/stories?group=${groupBy}&focus=${encodeURIComponent(key)}`;
    await navigator.clipboard.writeText(url);
    toast.success(`Link to "${key}" copied — share it with your class`);
  };

  const ERA_ORDER = ["Before 1500", "1500–1799", "1800s", "1900–1949", "1950–1999", "2000s — Today", "Undated"];
  const eraOf = (s) => {
    const y = typeof s.year === "number" ? s.year : parseInt(s.year, 10);
    if (Number.isNaN(y) || y == null) return "Undated";
    if (y < 1500) return "Before 1500";
    if (y < 1800) return "1500–1799";
    if (y < 1900) return "1800s";
    if (y < 1950) return "1900–1949";
    if (y < 2000) return "1950–1999";
    return "2000s — Today";
  };
  const grouped = () => {
    const map = {};
    for (const s of stories) {
      const key = groupBy === "region" ? (s.region || "Unspecified") : eraOf(s);
      (map[key] = map[key] || []).push(s);
    }
    const keys = Object.keys(map);
    if (groupBy === "era") keys.sort((a, b) => ERA_ORDER.indexOf(a) - ERA_ORDER.indexOf(b));
    else keys.sort((a, b) => a.localeCompare(b));
    return keys.map((k) => ({ key: k, items: map[k] }));
  };

  const renderCard = (s) => (
    <button
      key={s.id}
      onClick={() => setActive(s)}
      data-testid={`story-card-${s.id}`}
      className="group flex flex-col overflow-hidden border border-edge bg-ash text-left transition-colors hover:border-gold/50"
    >
      {s.photo_url ? (
        <div className="aspect-[16/10] overflow-hidden">
          <img src={photoSrc(s.photo_url)} alt="" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
        </div>
      ) : (
        <div className="flex aspect-[16/10] items-center justify-center bg-ink">
          <Feather className="h-8 w-8 text-edge" />
        </div>
      )}
      <div className="flex flex-1 flex-col p-5">
        <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.12em] text-gold">
          <Calendar className="h-3.5 w-3.5" /> {s.year_label}
        </div>
        <h3 className="mt-2 font-serif text-xl text-bone group-hover:text-gold">{s.title}</h3>
        <p className="mt-2 line-clamp-3 flex-1 text-sm leading-relaxed text-[#cfc9bd]">{s.body}</p>
        <div className="mt-4 flex items-center justify-between">
          <span className="flex items-center gap-1 text-xs text-muted"><MapPin className="h-3.5 w-3.5" /> {s.region}</span>
          <span className="flex items-center gap-3 text-xs text-muted">
            <span className="flex items-center gap-1"><Heart className="h-3.5 w-3.5" /> {s.like_count || 0}</span>
            <span className="flex items-center gap-1"><MessageCircle className="h-3.5 w-3.5" /> {s.note_count || 0}</span>
          </span>
        </div>
      </div>
    </button>
  );

  const groupBtn = (val, label, Icon) => (
    <button onClick={() => setGroup(val)} data-testid={`group-${val}`}
      className={`flex items-center gap-2 border px-4 py-2 font-mono text-[11px] uppercase tracking-[0.15em] transition-colors ${groupBy === val ? "border-gold bg-gold text-ink" : "border-edge text-bone hover:border-gold/60"}`}>
      <Icon className="h-3.5 w-3.5" /> {label}
    </button>
  );

  return (
    <div data-testid="stories-page">
      <div className="border-b border-edge bg-ash">
        <div className="mx-auto max-w-7xl px-6 py-20 md:px-12">
          <SectionLabel>Heritage Stories</SectionLabel>
          <h1 className="font-serif text-5xl font-semibold text-bone lg:text-6xl">The stories we carry</h1>
          <p className="mt-5 max-w-2xl text-lg text-muted">
            Personal and family histories from across the diaspora. Share a memory with a date and a place, and it
            will take its rightful spot on the Timeline of Truth.
          </p>
          <div className="mt-8">
            {user ? (
              <Button variant="gold" onClick={() => setShowForm(true)} data-testid="share-story-btn">
                <Feather className="h-4 w-4" /> Share your story
              </Button>
            ) : (
              <Button variant="outline" onClick={login} data-testid="story-signin-btn">
                Sign in to share your story
              </Button>
            )}
          </div>
        </div>
      </div>

      {loading ? (
        <Spinner />
      ) : stories.length === 0 ? (
        <div className="mx-auto max-w-3xl px-6 py-24 text-center">
          <p className="border border-dashed border-edge p-10 text-muted" data-testid="empty-stories">
            No heritage stories yet. Be the first to share one.
          </p>
        </div>
      ) : (
        <div className="mx-auto max-w-7xl px-6 py-16 md:px-12">
          <div className="mb-8 flex flex-wrap gap-2" data-testid="story-collections-controls">
            {groupBtn("none", "All Stories", LayoutGrid)}
            {groupBtn("region", "By Region", Globe2)}
            {groupBtn("era", "By Era", Hourglass)}
          </div>
          {groupBy === "none" ? (
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {stories.map(renderCard)}
            </div>
          ) : (
            <div className="space-y-12">
              {grouped().map((g) => (
                <div key={g.key} id={`group-${g.key}`} data-testid={`story-group-${g.key}`} style={{ scrollMarginTop: 90 }}>
                  <div className="mb-5 flex items-baseline gap-3">
                    <h2 className="font-serif text-2xl text-bone">{g.key}</h2>
                    <span className="font-mono text-xs text-muted">{g.items.length} {g.items.length === 1 ? "story" : "stories"}</span>
                    <button onClick={() => copyGroupLink(g.key)} data-testid={`group-share-${g.key}`}
                      title="Copy a link that opens straight to this group"
                      className="flex items-center gap-1 border border-edge px-2 py-1 text-xs text-muted transition-colors hover:border-gold hover:text-gold">
                      <Link2 className="h-3 w-3" /> Share group
                    </button>
                  </div>
                  <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                    {g.items.map(renderCard)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <AnimatePresence>
        {showForm && <StoryForm onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }} defaultAuthor={user?.name} />}
      </AnimatePresence>

      <AnimatePresence>
        {active && <StoryModal story={active} onClose={() => setActive(null)} canDelete={user?.user_id === active.user_id} onDelete={() => removeStory(active.id)} user={user} login={login} onCounts={updateCounts} />}
      </AnimatePresence>
    </div>
  );
}

function StoryForm({ onClose, onSaved, defaultAuthor }) {
  const fileRef = useRef();
  const [title, setTitle] = useState("");
  const [year, setYear] = useState("");
  const [region, setRegion] = useState(REGIONS[0]);
  const [body, setBody] = useState("");
  const [author, setAuthor] = useState(defaultAuthor || "");
  const [photoUrl, setPhotoUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const pickPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image"); return; }
    setUploading(true);
    try {
      const res = await api.post("/stories/photo", file, { headers: { "Content-Type": file.type } });
      setPhotoUrl(res.data.photo_url);
      toast.success("Photo added");
    } catch {
      toast.error("Photo upload failed");
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !body.trim() || !year) { toast.error("Title, year and story are required"); return; }
    setSaving(true);
    try {
      await api.post("/stories", {
        title: title.trim(), body: body.trim(), region, year: Number(year),
        author_name: author.trim(), photo_url: photoUrl,
      });
      toast.success("Your story is live");
      onSaved();
    } catch {
      toast.error("Could not save your story");
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div
      className="fixed inset-0 z-[80] flex items-start justify-center overflow-y-auto bg-black/70 p-4 backdrop-blur-sm"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      onClick={onClose} data-testid="story-form-modal"
    >
      <motion.form
        onClick={(e) => e.stopPropagation()} onSubmit={submit}
        initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 20, opacity: 0 }}
        className="my-10 w-full max-w-lg border border-edge bg-ink p-6 md:p-8"
      >
        <div className="mb-6 flex items-center justify-between">
          <h2 className="font-serif text-2xl text-bone">Share your heritage story</h2>
          <button type="button" onClick={onClose} className="text-muted hover:text-bone"><X className="h-5 w-5" /></button>
        </div>

        <label className="mb-1 block font-mono text-[11px] uppercase tracking-[0.15em] text-muted">Title</label>
        <input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="story-title-input"
          placeholder="e.g. My great-grandfather's journey"
          className="mb-4 w-full border border-edge bg-ash px-4 py-3 text-bone placeholder:text-muted focus:border-gold focus:outline-none" />

        <div className="mb-4 grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1 block font-mono text-[11px] uppercase tracking-[0.15em] text-muted">Year (date)</label>
            <input type="number" value={year} onChange={(e) => setYear(e.target.value)} data-testid="story-year-input"
              placeholder="e.g. 1957"
              className="w-full border border-edge bg-ash px-4 py-3 text-bone placeholder:text-muted focus:border-gold focus:outline-none" />
          </div>
          <div>
            <label className="mb-1 block font-mono text-[11px] uppercase tracking-[0.15em] text-muted">Region</label>
            <select value={region} onChange={(e) => setRegion(e.target.value)} data-testid="story-region-select"
              className="w-full border border-edge bg-ash px-4 py-3 text-bone focus:border-gold focus:outline-none">
              {REGIONS.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
        </div>

        <label className="mb-1 block font-mono text-[11px] uppercase tracking-[0.15em] text-muted">Your name</label>
        <input value={author} onChange={(e) => setAuthor(e.target.value)} data-testid="story-author-input"
          placeholder="How you'd like to be credited"
          className="mb-4 w-full border border-edge bg-ash px-4 py-3 text-bone placeholder:text-muted focus:border-gold focus:outline-none" />

        <label className="mb-1 block font-mono text-[11px] uppercase tracking-[0.15em] text-muted">The story</label>
        <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={5} data-testid="story-body-input"
          placeholder="Share the memory, the people, the place…"
          className="mb-4 w-full resize-y border border-edge bg-ash px-4 py-3 text-bone placeholder:text-muted focus:border-gold focus:outline-none" />

        <input ref={fileRef} type="file" accept="image/*" onChange={pickPhoto} className="hidden" data-testid="story-photo-input" />
        <div className="mb-6 flex items-center gap-3">
          <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
            className="flex items-center gap-2 border border-edge px-4 py-2.5 text-sm text-bone hover:border-gold disabled:opacity-50" data-testid="story-photo-btn">
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}
            {photoUrl ? "Change photo" : "Add a photo (optional)"}
          </button>
          {photoUrl && <img src={photoSrc(photoUrl)} alt="" className="h-11 w-11 border border-edge object-cover" />}
        </div>

        <Button type="submit" variant="gold" className="w-full" disabled={saving} data-testid="story-submit-btn">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Publish story
        </Button>
      </motion.form>
    </motion.div>
  );
}

function StoryModal({ story, onClose, canDelete, onDelete, user, login, onCounts }) {
  const [likes, setLikes] = useState(story.like_count || 0);
  const [liked, setLiked] = useState(false);
  const [notes, setNotes] = useState([]);
  const [noteText, setNoteText] = useState("");
  const [posting, setPosting] = useState(false);

  useEffect(() => {
    api.get(`/stories/${story.id}/reactions`).then((r) => {
      setLikes(r.data.likes); setLiked(r.data.liked); setNotes(r.data.notes);
    }).catch(() => {});
  }, [story.id]);

  const toggleLike = async () => {
    if (!user) return login();
    try {
      const r = await api.post(`/stories/${story.id}/like`);
      setLikes(r.data.likes); setLiked(r.data.liked);
      onCounts?.(story.id, { like_count: r.data.likes });
    } catch { toast.error("Could not react"); }
  };

  const postNote = async (e) => {
    e.preventDefault();
    if (!user) return login();
    if (!noteText.trim()) return;
    setPosting(true);
    try {
      const r = await api.post(`/stories/${story.id}/notes`, { text: noteText.trim() });
      const next = [r.data, ...notes];
      setNotes(next);
      setNoteText("");
      onCounts?.(story.id, { note_count: next.length });
      toast.success("Note added");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add note");
    } finally {
      setPosting(false);
    }
  };

  return (
    <motion.div
      className="fixed inset-0 z-[80] flex items-start justify-center overflow-y-auto bg-black/70 p-4 backdrop-blur-sm"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      onClick={onClose} data-testid="story-detail-modal"
    >
      <motion.div
        onClick={(e) => e.stopPropagation()}
        initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 20, opacity: 0 }}
        className="my-10 w-full max-w-2xl border border-edge bg-ink"
      >
        {story.photo_url && (
          <div className="max-h-80 overflow-hidden border-b border-edge">
            <img src={photoSrc(story.photo_url)} alt="" className="h-full w-full object-cover" />
          </div>
        )}
        <div className="p-6 md:p-8">
          <div className="flex items-start justify-between gap-4">
            <div className="flex flex-wrap items-center gap-2">
              <Pill tone="gold">{story.year_label}</Pill>
              <Pill tone="muted">{story.region}</Pill>
              <Pill tone="forest">Heritage Story</Pill>
            </div>
            <button onClick={onClose} data-testid="story-modal-close" className="text-muted hover:text-bone"><X className="h-6 w-6" /></button>
          </div>
          <h2 className="mt-4 font-serif text-3xl text-bone">{story.title}</h2>
          <p className="mt-1 text-sm text-muted">Shared by {story.author_name}</p>
          <p className="mt-5 whitespace-pre-line leading-relaxed text-[#cfc9bd]">{story.body}</p>

          {/* REACTIONS */}
          <div className="mt-8 border-t border-edge pt-6">
            <div className="flex flex-wrap items-center gap-2">
              <button onClick={toggleLike} data-testid="story-like-btn"
                className={`flex items-center gap-2 border px-4 py-2 text-sm transition-colors ${liked ? "border-blood bg-blood/10 text-[#e07a72]" : "border-edge text-bone hover:border-blood/60"}`}>
                <Heart className={`h-4 w-4 ${liked ? "fill-current" : ""}`} />
                <span data-testid="story-like-count">{likes}</span> {likes === 1 ? "heart" : "hearts"}
              </button>
              <button
                onClick={async () => { await navigator.clipboard.writeText(`${window.location.origin}/stories/${story.id}`); toast.success("Link copied — share it anywhere"); }}
                data-testid="story-copy-link-btn"
                className="flex items-center gap-2 border border-edge px-4 py-2 text-sm text-bone transition-colors hover:border-gold hover:text-gold">
                <Link2 className="h-4 w-4" /> Copy share link
              </button>
              <a href={`/stories/${story.id}`} data-testid="story-open-page-btn"
                className="flex items-center gap-2 border border-edge px-4 py-2 text-sm text-bone transition-colors hover:border-gold hover:text-gold">
                <ExternalLink className="h-4 w-4" /> Open story page
              </a>
            </div>

            <div className="mt-6">
              <h3 className="mb-3 flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.15em] text-gold">
                <MessageCircle className="h-4 w-4" /> Notes ({notes.length})
              </h3>
              <form onSubmit={postNote} className="mb-4 flex gap-2" data-testid="story-note-form">
                <input value={noteText} onChange={(e) => setNoteText(e.target.value)} maxLength={500}
                  placeholder={user ? "Leave a short note…" : "Sign in to leave a note"}
                  data-testid="story-note-input"
                  className="flex-1 border border-edge bg-ash px-4 py-2.5 text-sm text-bone placeholder:text-muted focus:border-gold focus:outline-none" />
                <button type="submit" disabled={posting} data-testid="story-note-submit"
                  className="flex items-center gap-1 border border-gold bg-gold px-4 text-sm font-semibold text-ink disabled:opacity-50">
                  {posting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                </button>
              </form>
              <div className="space-y-3">
                {notes.map((n) => (
                  <div key={n.id} className="border-l-2 border-edge pl-4" data-testid={`story-note-${n.id}`}>
                    <p className="text-sm text-[#cfc9bd]">{n.text}</p>
                    <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.12em] text-muted">— {n.author_name}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {canDelete && (
            <button onClick={onDelete} data-testid="story-delete-btn" className="mt-8 flex items-center gap-2 text-sm text-muted hover:text-blood">
              <Trash2 className="h-4 w-4" /> Delete this story
            </button>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}
